import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../../hooks/document/documentsApi';
import { reactQueryFinanceDefaults } from '../finance/queryDefaults';

// Normalize the nested API response into a flat, predictable shape
const selectDocuments = (response) => {
  const data = response?.data ?? response;
  return {
    documents: data?.documents ?? [],
    total: data?.total ?? 0,
  };
};

export const useDocuments = ({ category, search, limit = 50, offset = 0 } = {}) => {
  return useQuery({
    queryKey: ['documents', { category, search, limit, offset }],
    queryFn: () => documentsApi.listDocuments({ category, search, limit, offset }),
    select: selectDocuments,
    ...reactQueryFinanceDefaults,
  });
};

export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, displayName, category }) =>
      documentsApi.uploadDocument(file, displayName, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export const useDocumentPreview = (documentId, enabled = true) => {
  return useQuery({
    queryKey: ['document-preview', documentId],
    queryFn: () => documentsApi.getPreviewUrl(documentId),
    enabled: enabled && !!documentId,
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, displayName, category, tags, bidStatus, engagementType, engagementId }) =>
      documentsApi.updateDocument(documentId, displayName, category, tags, bidStatus, engagementType, engagementId),
    onSuccess: (data, { documentId }) => {
      // Optimistically update the cached document instead of full refetch
      queryClient.setQueriesData({ queryKey: ['documents'] }, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          documents: oldData.documents.map((doc) =>
            doc.id === documentId
              ? { ...doc, ...data?.data ?? {} }
              : doc
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId) => documentsApi.deleteDocument(documentId),
    onSuccess: (data, documentId) => {
      queryClient.setQueriesData({ queryKey: ['documents'] }, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          documents: oldData.documents.filter((doc) => doc.id !== documentId),
          total: oldData.total - 1,
        };
      });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export const useDownloadDocument = () => {
  return {
    downloadDocument: async (documentId, filename) => {
      try {
        const response = await documentsApi.getDownloadUrl(documentId);
        const url = response?.data?.downloadUrl ?? response?.downloadUrl;
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return { success: true };
      } catch (error) {
        console.error('Download failed:', error);
        return { success: false, error };
      }
    },
  };
};

export const useExtractProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, engagementId }) =>
      documentsApi.extractProposal(documentId, engagementId),
    onSuccess: (data, { documentId }) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document-extraction', documentId] });
    },
    onError: (error) => {
      console.error('❌ Proposal extraction failed:', error);
    },
  });
};

export const useGetExtractionResult = (documentId, enabled = true) => {
  return useQuery({
    queryKey: ['document-extraction', documentId],
    queryFn: () => documentsApi.getExtractionResult(documentId),
    enabled: enabled && !!documentId,
    ...reactQueryFinanceDefaults,
  });
};